// /////////////////////////////////////////////////////////////
// [ファイル] NotificationProvider.tsx
// [概要] 処理結果（成功・失敗・情報）を非ブロッキングな Snackbar で
//        通知するためのコンテキスト。alert() の置き換えとして利用する。
//
// 使用例:
//   const notify = useNotification();
//   notify.success('保存しました');
//   notify.error('保存に失敗しました');
//   notify.show('処理中です', 'info');
// /////////////////////////////////////////////////////////////
import React, { createContext, useCallback, useContext, useState } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface NotificationApi {
  // 任意の severity で通知する基底メソッド
  show: (message: string, severity?: AlertColor) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
}

const noop = () => {};
const NotificationContext = createContext<NotificationApi>({
  show: noop,
  success: noop,
  error: noop,
  info: noop,
  warning: noop,
});

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const show = useCallback((message: string, severity: AlertColor = 'info') => {
    setState({ open: true, message, severity });
  }, []);

  const handleClose = useCallback((_?: unknown, reason?: string) => {
    // クリックアウェイでは閉じない（自動非表示か × ボタンのみで閉じる）
    if (reason === 'clickaway') return;
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  // severity 別のショートカット。show を包むだけ。
  const api: NotificationApi = {
    show,
    success: useCallback((m: string) => show(m, 'success'), [show]),
    error: useCallback((m: string) => show(m, 'error'), [show]),
    info: useCallback((m: string) => show(m, 'info'), [show]),
    warning: useCallback((m: string) => show(m, 'warning'), [show]),
  };

  return (
    <NotificationContext.Provider value={api}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={3500}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => handleClose()}
          severity={state.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

/**
 * 処理結果を Snackbar で通知するフック。
 */
export const useNotification = () => useContext(NotificationContext);
